import type { ReactNode } from 'react';
import { PortableText } from '@portabletext/react';
import dayjs from 'dayjs';

import type { PublishedLineup } from '@api-service/predicted-lineups';
import {
  ARTICLE_TOKENS,
  SCROLL_MARGIN_CLASS
} from '@components/article/design-tokens';
import { buildArticlePortableTextComponents } from '@components/article/portable-text-components';
import { AvailabilityList } from '@components/predicted-lineups/AvailabilityList';
import { LineupPitch } from '@components/predicted-lineups/LineupPitch';
import { TeamBadge } from '@components/transfers/TeamBadge';
import type { ArticleBlockData } from '@lib/article-blocks/types';
import { resolveLastUpdated } from '@lib/predicted-lineups/compose';
import type {
  ClubDescriptor,
  ClubHeading
} from '@lib/predicted-lineups/compose';
import type { PlayerFactsMap } from '@lib/predicted-lineups/player-facts';
import type { SanityPredictedLineupTeamSection } from '@lib/sanity';

const KICKOFF_FORMAT = 'ddd D MMM, HH:mm';
const UPDATED_FORMAT = 'ddd D MMM, HH:mm';

type ClubSectionProps = {
  club: ClubDescriptor;
  heading: ClubHeading;
  lineup?: PublishedLineup;
  playerFacts: PlayerFactsMap;
  /** The club's words. Absent for a gated club the visitor cannot read. */
  words?: SanityPredictedLineupTeamSection;
  blockData: ArticleBlockData;
  /** Shown in place of the words when they are not available to this visitor. */
  wordsFallback?: ReactNode;
};

/**
 * One club on the page: heading (which feeds the table of contents), the
 * fixture and freshness line, the pitch from the API, then the editor's words.
 */
export const ClubSection = ({
  club,
  heading,
  lineup,
  playerFacts,
  words,
  blockData,
  wordsFallback
}: ClubSectionProps) => {
  const lastUpdated = resolveLastUpdated(words, lineup);
  const clubName = club.teamName ?? club.teamShortName;
  const notes = words?.notes ?? [];
  const doubtOptaIds = new Set(
    (words?.doubts ?? []).map((entry) => entry.player.optaUuid)
  );
  const hasAvailability = Boolean(
    words &&
      [words.ruledOut, words.doubts, words.suspensions].some(
        (list) => list && list.length > 0
      )
  );

  return (
    <section
      id={heading.id}
      className={`${SCROLL_MARGIN_CLASS} mt-14`}
      data-cy="club-section"
      data-team={club.teamShortName}
    >
      <h2
        className={`m-0 flex items-start gap-3 font-heading text-[22px] font-bold leading-[1.15] sm:text-[26px] ${ARTICLE_TOKENS.ink}`}
      >
        <TeamBadge
          shortName={club.teamShortName}
          size="lg"
          className="!mb-0 mt-px !h-7 !w-7 shrink-0 sm:!h-9 sm:!w-9"
        />
        <span>{heading.text}</span>
      </h2>
      <p
        className={`mb-0 mt-2 font-body text-[13px] ${ARTICLE_TOKENS.muted}`}
        data-cy="club-meta"
      >
        {club.kickoffAt && (
          <span>Kick-off {dayjs(club.kickoffAt).format(KICKOFF_FORMAT)}</span>
        )}
        {club.kickoffAt && lastUpdated && <span aria-hidden> · </span>}
        {lastUpdated && (
          <span>Last updated {dayjs(lastUpdated).format(UPDATED_FORMAT)}</span>
        )}
      </p>
      <LineupPitch
        lineup={lineup}
        playerFacts={playerFacts}
        clubName={clubName}
        doubtOptaIds={doubtOptaIds}
      />
      {words ? (
        <>
          {notes.length > 0 && (
            <div className="content-body" data-cy="club-notes">
              <PortableText
                value={notes}
                components={buildArticlePortableTextComponents(blockData, [])}
              />
            </div>
          )}
          {hasAvailability && (
            <div
              className={`mt-5 flex flex-col gap-3 rounded-[14px] border p-4 ${ARTICLE_TOKENS.rule} ${ARTICLE_TOKENS.surfaceTint}`}
              data-cy="club-availability"
            >
              <AvailabilityList
                title="Ruled out"
                entries={words.ruledOut}
                playerFacts={playerFacts}
                tone="danger"
                testId="club-ruled-out"
              />
              <AvailabilityList
                title="Doubts"
                entries={words.doubts}
                playerFacts={playerFacts}
                tone="warning"
                testId="club-doubts"
              />
              <AvailabilityList
                title="Suspended"
                entries={words.suspensions}
                playerFacts={playerFacts}
                tone="neutral"
                testId="club-suspensions"
              />
            </div>
          )}
        </>
      ) : (
        wordsFallback
      )}
    </section>
  );
};
